// Which case documents an export includes (sc-10997). Mirrors the server's
// ExportFilter (server/services/export/export_filter.py) so the dialog's counts
// match what the ZIP / PDF will hold.
import type { CaseDocument, ReviewDecision } from '../../types';
import { REVIEW_DECISIONS } from '../../types';

export type ExportFormat = 'zip' | 'pdf';

/** A review decision, or `undecided` for documents nobody has decided yet. */
export type ExportDecision = ReviewDecision | 'undecided';

export const EXPORT_DECISIONS: ExportDecision[] = [...REVIEW_DECISIONS, 'undecided'];

export interface ExportSelection {
  decisions: ExportDecision[];
  /** Only documents whose redaction was finalized (status `completed`). */
  finalizedOnly: boolean;
  /** ZIP only: a document finalized with no redactions goes in as its original
   *  upload instead of its redacted copy (sc-11470). */
  keepOriginalFormat: boolean;
}

/**
 * The dialog's starting point: only the responsive documents once anything is
 * marked responsive, otherwise every decision (the export as it was before
 * decisions existed). Finalized only either way, and files with no redactions
 * keep their original format.
 */
export function defaultExportSelection(documents: CaseDocument[]): ExportSelection {
  const anyResponsive = documents.some((d) => d.review_decision === 'responsive');
  return {
    decisions: anyResponsive ? ['responsive'] : [...EXPORT_DECISIONS],
    finalizedOnly: true,
    keepOriginalFormat: true,
  };
}

/** A ZIP holds redacted files, which only finalized documents have. */
export function effectiveFinalizedOnly(format: ExportFormat, selection: ExportSelection): boolean {
  return format === 'zip' || selection.finalizedOnly;
}

/**
 * Whether a document counts as finalized for this export. A ZIP also needs the
 * redacted file: a document finalized with nothing to burn has none, and the
 * server leaves it out of the redacted ZIP.
 */
function isExportable(doc: CaseDocument, format: ExportFormat): boolean {
  return doc.status === 'completed' && (format !== 'zip' || Boolean(doc.has_redacted_version));
}

export function exportDecisionOf(doc: CaseDocument): ExportDecision {
  return doc.review_decision ?? 'undecided';
}

export interface ExportCounts {
  /** Exportable documents (containers never are) per decision. */
  byDecision: Record<ExportDecision, number>;
  /** Documents the export will include. */
  included: number;
  /** Documents with a chosen decision left out because they aren't finalized (for
   *  a ZIP: have no redacted file). */
  notFinalized: CaseDocument[];
}

export function exportCounts(
  documents: CaseDocument[],
  format: ExportFormat,
  selection: ExportSelection
): ExportCounts {
  const byDecision = Object.fromEntries(EXPORT_DECISIONS.map((d) => [d, 0])) as Record<ExportDecision, number>;
  const finalizedOnly = effectiveFinalizedOnly(format, selection);
  let included = 0;
  const notFinalized: CaseDocument[] = [];
  for (const doc of documents) {
    if (doc.is_container) continue;
    const decision = exportDecisionOf(doc);
    byDecision[decision] += 1;
    if (!selection.decisions.includes(decision)) continue;
    if (finalizedOnly && !isExportable(doc, format)) {
      notFinalized.push(doc);
      continue;
    }
    included += 1;
  }
  return { byDecision, included, notFinalized };
}

/**
 * Query params for the export endpoints. Every decision chosen means no decision
 * filter, so the request is the same as before decisions existed.
 */
export function exportQueryParams(format: ExportFormat, selection: ExportSelection): Record<string, string | boolean> {
  const params: Record<string, string | boolean> = {};
  if (!EXPORT_DECISIONS.every((d) => selection.decisions.includes(d))) {
    params.decisions = selection.decisions.join(',');
  }
  if (effectiveFinalizedOnly(format, selection)) params.finalized_only = true;
  if (format === 'zip' && selection.keepOriginalFormat) params.keep_original_format = true;
  return params;
}
